import { parse, format } from 'date-fns';
import { parseDateOnly } from '@/lib/utils';
import type {
  HiAttemptFormValues,
  HiAttemptGetResponse,
  HiAttemptMedia,
  HiAttemptTenantPayload,
} from './hiAttempt.schema';

export const isWithinEditableWindow = (attempt: HiAttemptGetResponse): boolean => {
  const createdAt = new Date(attempt.createdAt);
  const editableUntil = new Date(createdAt);
  editableUntil.setFullYear(editableUntil.getFullYear() + 1);
  return new Date() <= editableUntil;
};

export const getAttemptMedia = (attempt: HiAttemptGetResponse): HiAttemptMedia[] => attempt.media ?? [];

const buildMediaUrl = (media?: HiAttemptMedia['media']): string => {
  if (!media?.baseUrl || !media.path) return '';
  const base = media.baseUrl.replace(/\/$/, '');
  const path = media.path.replace(/^\//, '');
  return `${base}/${path}`;
};

export type HiAttemptImagePreview = {
  objectUrl: string;
  name: string;
  mediaId: string;
  isExisting: true;
};

export const buildAttemptImagePreviews = (attempt: HiAttemptGetResponse): HiAttemptImagePreview[] =>
  getAttemptMedia(attempt).map((m) => ({
    objectUrl: buildMediaUrl(m.media),
    name: m.media?.path?.split('/').pop() ?? m.mediaId,
    mediaId: m.mediaId,
    isExisting: true,
  }));

type HiAttemptTenantFormEntry = {
  name: string;
  floor?: string | null;
  relative?: 'YES' | 'NO' | null;
  lease?: 'YES' | 'NO' | null;
  rentAmount?: number | string | null;
};

/** Map form tenants to API tenant objects, including relative/lease/rentAmount when set. */
export const mapHiAttemptTenantsToPayload = (
  tenants: HiAttemptTenantFormEntry[] | null | undefined
): HiAttemptTenantPayload[] | null => {
  if (!tenants?.length) return null;

  return tenants.map((t) => {
    const mapped: HiAttemptTenantPayload = {
      name: t.name,
      floor: t.floor ?? null,
    };
    if (t.relative === 'YES' || t.relative === 'NO') {
      mapped.relative = t.relative;
    }
    if (t.lease === 'YES' || t.lease === 'NO') {
      mapped.lease = t.lease;
    }
    if (t.rentAmount != null && t.rentAmount !== '') {
      const rentAmount = Number(t.rentAmount);
      if (!Number.isNaN(rentAmount)) {
        mapped.rentAmount = rentAmount;
      }
    }
    return mapped;
  });
};

export const buildDefaultValues = (attempt: HiAttemptGetResponse): Partial<HiAttemptFormValues> => {
  // Convert stored HH:mm time to 12-hour format for the form
  let timeOfAttempt = attempt.timeOfAttempt ?? '';
  if (timeOfAttempt && /^\d{2}:\d{2}$/.test(timeOfAttempt)) {
    try {
      const parsed = parse(timeOfAttempt, 'HH:mm', new Date());
      timeOfAttempt = format(parsed, 'hh:mm a');
    } catch {
      // keep original if parse fails
    }
  }

  return {
    dateOfAttempt: attempt.dateOfAttempt ? parseDateOnly(attempt.dateOfAttempt) : undefined,
    timeOfAttempt,
    ownerAddressLine1: attempt.ownerAddressLine1 ?? undefined,
    ownerAddressLine2: attempt.ownerAddressLine2 ?? undefined,
    ownerCity: attempt.ownerCity ?? undefined,
    ownerState: attempt.ownerState ?? undefined,
    ownerZipCode: attempt.ownerZipCode ?? undefined,
    ownerPhone: attempt.ownerPhone ?? undefined,
    maritalStatus: attempt.maritalStatus ?? undefined,
    spouseName: attempt.spouseName ?? undefined,
    ownerOccupied: attempt.ownerOccupied ?? undefined,
    vacant: attempt.vacant ?? undefined,
    bothOwnersResideTogether: attempt.bothOwnersResideTogether ?? undefined,
    coOwnerName: attempt.coOwnerName ?? undefined,
    coOwnerAddressLine1: attempt.coOwnerAddressLine1 ?? undefined,
    coOwnerAddressLine2: attempt.coOwnerAddressLine2 ?? undefined,
    coOwnerCity: attempt.coOwnerCity ?? undefined,
    coOwnerState: attempt.coOwnerState ?? undefined,
    coOwnerZipCode: attempt.coOwnerZipCode ?? undefined,
    defendantInArmedForces: attempt.defendantInArmedForces ?? undefined,
    tenantOccupied: attempt.tenantOccupied ?? undefined,
    tenants: attempt.tenants?.map((t, i) => ({
      name: t.name,
      floor: t.floor ?? null,
      relative: t.relative ?? (i === 0 ? (attempt.relative ?? undefined) : undefined),
      lease: t.lease ?? (i === 0 ? (attempt.lease ?? undefined) : undefined),
      rentAmount:
        t.rentAmount != null
          ? Number(t.rentAmount)
          : i === 0 && attempt.rentAmount != null
            ? Number(attempt.rentAmount)
            : undefined,
    })) ?? [],
    houseExterior: attempt.houseExterior as HiAttemptFormValues['houseExterior'],
    houseExteriorOther: attempt.houseExteriorOther ?? undefined,
    houseType: attempt.houseType as HiAttemptFormValues['houseType'],
    numberOfUnits: attempt.numberOfUnits,
    garage: attempt.garage as HiAttemptFormValues['garage'],
    sale: attempt.sale as HiAttemptFormValues['sale'],
    saleBy: attempt.saleBy ?? undefined,
    salePhone: attempt.salePhone ?? undefined,
    comments: attempt.comments ?? undefined,
    mediaIds: getAttemptMedia(attempt).map((m) => m.mediaId),
  };
};
